import React, { useState, useEffect } from 'react';
import { 
  Search, Building, User, Mail, Calendar, ChevronRight, 
  Filter, Users, Pencil, X, CheckCircle, Clock, AlertCircle,
   Download, Send, Eye, Zap, TrendingUp, Star, Activity,CheckCircle2,Trash2 
} from 'lucide-react';
import connectionService from '../services/connectionService';
import QuoteProgressService from '../services/quoteProgressService';
import CustomerForm from './AddConnection';
import StepComponent  from './StepComponent';

const ExistingComponent = ({ onConnectionSelect, onClose }) => {
  const [connections, setConnections] = useState([]);
  const [filteredConnections, setFilteredConnections] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('date'); 
  const [loading, setLoading] = useState(true);
  const [last6MonthsOnly, setLast6MonthsOnly] = useState(true);
  
  const [currentUser, setCurrentUser] = useState({ id: null, name: '', role: '' });
  const [showModal, setShowModal] = useState(true);
  const [connectionProgress, setConnectionProgress] = useState({});
  const [progressData, setProgressData] = useState({});
  
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [selectedStep, setSelectedStep] = useState({ connectionId: null, stepName: null });
  
  const userRole = localStorage.getItem('Role');
  const isAdmin = userRole === 'Admin';
  const isTeamMember = userRole === 'Team Member'; // ← adjust to match your exact role name
  const canViewProgress = !isTeamMember; // everyone except team members can see progress

  const [editingConnection, setEditingConnection] = useState(null);
  const [formData, setFormData] = useState({});

  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleteSuccess, setDeleteSuccess] = useState(false);

  useEffect(() => {
    const userId = localStorage.getItem('userId');
    const userName = localStorage.getItem('userName');
    const userRole = localStorage.getItem('Role');

    setCurrentUser({
      id: userId,
      name: userName,
      role: userRole
    });
  }, []);

  const STEP_SEQUENCE = [
    { 
      name: 'system_config', 
      dbName: 'system_config',
      label: 'System Config', 
      color: 'blue',
      icon: <Zap className="h-3 w-3" />,
      gradient: 'from-blue-400 to-blue-600',
      isInitial: true,
      stepType: 'base'
    },
    { 
      name: 'quote-downloaded', 
      dbName: 'quote',
      actionType: 'downloaded',
      label: 'Quote Downloaded', 
      color: 'orange',
      icon: <Download className="h-3 w-3" />,
      gradient: 'from-orange-400 to-orange-600',
      stepType: 'action'
    },
    { 
      name: 'quote-emailed', 
      dbName: 'quote',
      actionType: 'emailed',
      label: 'Quote Sent', 
      color: 'orange',
      icon: <Send className="h-3 w-3" />,
      gradient: 'from-orange-500 to-orange-700',
      stepType: 'action'
    },
    { 
      name: 'contract-downloaded', 
      dbName: 'contract',
      actionType: 'downloaded',
      label: 'Contract Downloaded', 
      color: 'red',
      icon: <Download className="h-3 w-3" />,
      gradient: 'from-red-400 to-red-600',
      stepType: 'action'
    },
    { 
      name: 'contract-emailed', 
      dbName: 'contract',
      actionType: 'emailed',
      label: 'Contract Sent', 
      color: 'red',
      icon: <Send className="h-3 w-3" />,
      gradient: 'from-red-500 to-red-700',
      stepType: 'action'
    },
    { 
      name: 'rental-downloaded', 
      dbName: 'rental',
      actionType: 'downloaded',
      label: 'Rental Downloaded', 
      color: 'green',
      icon: <Download className="h-3 w-3" />,
      gradient: 'from-green-400 to-green-600',
      stepType: 'action'
    },
    { 
      name: 'rental-emailed', 
      dbName: 'rental',
      actionType: 'emailed',
      label: 'Rental Sent', 
      color: 'green',
      icon: <Send className="h-3 w-3" />,
      gradient: 'from-green-500 to-green-700',
      stepType: 'action'
    }
  ];

  const loadData = async () => {
    try {
      const connectionsResponse = await connectionService.getAllConnections();
      let connectionsData = Array.isArray(connectionsResponse?.data) 
        ? connectionsResponse.data 
        : [];

      const role = currentUser?.role?.toLowerCase();

      if (role !== 'admin' && role !== 'manager') {
        connectionsData = connectionsData.filter(
          connection => connection?.salesman_id?.toString() === currentUser?.id?.toString()
        );
      }

      setConnections(connectionsData);
      setFilteredConnections(connectionsData);
    } catch (error) {
      console.error('Error loading data:', error);
      setConnections([]);
      setFilteredConnections([]);
    }
  };

  useEffect(() => {
    if (currentUser.id) {
      const loadConnections = async () => {
        setLoading(true);
        await loadData();
        setLoading(false);
      };
      loadConnections();
    }
  }, [currentUser]);

  const getMainStepFromStepName = (stepName) => {
    if (stepName.includes('rental')) return 'rental';
    if (stepName.includes('contract')) return 'contract';
    if (stepName.includes('quote')) return 'quote';
    return stepName;
  };

  const handleConnectionSelect = (params) => {
    let connection, stepName, isAdminParam;
    
    if (params && params.connection) {
      connection = params.connection;
      stepName = params.stepName;
      isAdminParam = params.isAdmin;
    } else {
      connection = params;
      stepName = null;
      isAdminParam = isAdmin;
    }

    let stepToOpen = 'system_config';

    if (isAdminParam) {
      if (stepName) {
        stepToOpen = getMainStepFromStepName(stepName);
      } else {
        const progress = getProgressLevel(connection.id);
        stepToOpen = getMainStepFromStepName(progress.currentStep);
        if (progress.stepLabel === 'All Steps Completed') {
          stepToOpen = 'rental';
        }
      }
    } else {
      stepToOpen = 'system_config';
    }

    if (typeof onConnectionSelect === 'function') {
      onConnectionSelect(connection, stepToOpen);
    } else {
      console.error('onConnectionSelect is not a function');
    }

    if (typeof onClose === 'function') {
      onClose();
    }
  };

  const handleStepClick = (connection, stepName) => {
    setSelectedStep({ connectionId: connection.id, stepName: stepName });
    const mainStep = getMainStepFromStepName(stepName);
    handleConnectionSelect({ 
      connection, 
      stepName: mainStep, 
      isAdmin: isAdmin 
    });
  };

  const isActionStepCompleted = (step, progress) => {
    if (step.stepType === 'base') {
      const baseProgress = progress.find(p => p.step_name === step.dbName);
      return baseProgress?.completed || false;
    }
    
    const stepProgress = progress.find(p => p.step_name === step.dbName);
    if (!stepProgress) return false;
    
    return stepProgress.completed && stepProgress.actions?.[step.actionType] === true;
  };

  const getStepDisplayName = (step, progress) => {
    if (step.stepType === 'base') {
      return step.label;
    }
    
    const stepProgress = progress.find(p => p.step_name === step.dbName);
    if (!stepProgress) return step.label;
    
    const hasDownloaded = stepProgress.actions?.downloaded;
    const hasEmailed = stepProgress.actions?.emailed;
    
    if (step.dbName === 'quote') {
      if (hasDownloaded && hasEmailed) return 'Quote Downloaded & Sent';
      if (hasDownloaded) return 'Quote Downloaded';
      if (hasEmailed) return 'Quote Sent';
    }
    if (step.dbName === 'contract') {
      if (hasDownloaded && hasEmailed) return 'Contract Downloaded & Sent';
      if (hasDownloaded) return 'Contract Downloaded';
      if (hasEmailed) return 'Contract Sent';
    }
    if (step.dbName === 'rental') {
      if (hasDownloaded && hasEmailed) return 'Rental Downloaded & Sent';
      if (hasDownloaded) return 'Rental Downloaded';
      if (hasEmailed) return 'Rental Sent';
    }
    
    return step.label;
  };

  const getProgressLevel = (connectionId) => {
    const progress = progressData[connectionId] || [];
    const totalSteps = STEP_SEQUENCE.length;

    const stepDetails = STEP_SEQUENCE.map(step => {
      const completed = isActionStepCompleted(step, progress);
      const displayLabel = getStepDisplayName(step, progress);
      
      return {
        ...step,
        completed,
        displayLabel,
        hasActivity: completed,
        actions: step.stepType === 'action' ? 
          progress.find(p => p.step_name === step.dbName)?.actions || {} 
          : {}
      };
    });

    const isBrandNew = progress.length === 0;

    if (isBrandNew) {
      return {
        level: 'system_config',
        color: 'blue',
        percentage: 0,
        currentStep: 'system_config',
        stepLabel: 'System Config',
        stepIndex: 0,
        completedSteps: 0,
        hasActivity: false,
        isFullyComplete: false,
        isBrandNew: true,
        stepDetails: stepDetails
      };
    }

    let currentStepIndex = 0;
    let currentStepName = 'system_config';
    let currentStepLabel = 'System Config';
    let currentStepColor = 'blue';

    let highestCompletedIndex = -1;
    for (let i = STEP_SEQUENCE.length - 1; i >= 0; i--) {
      if (stepDetails[i].completed) {
        highestCompletedIndex = i;
        break;
      }
    }

    if (highestCompletedIndex === STEP_SEQUENCE.length - 1) {
      currentStepIndex = STEP_SEQUENCE.length - 1;
      currentStepName = STEP_SEQUENCE[currentStepIndex].name;
      currentStepLabel = 'All Steps Completed';
      currentStepColor = 'green';
    } else if (highestCompletedIndex >= 0) {
      currentStepIndex = highestCompletedIndex;
      currentStepName = STEP_SEQUENCE[currentStepIndex].name;
      currentStepLabel = stepDetails[currentStepIndex].displayLabel;
      currentStepColor = STEP_SEQUENCE[currentStepIndex].color;
    } else {
      currentStepIndex = 0;
      currentStepName = 'system_config';
      currentStepLabel = 'System Config';
      currentStepColor = 'blue';
    }

    const completedSteps = stepDetails.filter(step => step.completed).length;
    const progressPercentage = Math.round((completedSteps / totalSteps) * 100);

    return {
      level: currentStepName,
      color: currentStepColor,
      percentage: progressPercentage,
      currentStep: currentStepName,
      stepLabel: currentStepLabel,
      stepIndex: currentStepIndex,
      completedSteps: completedSteps,
      hasActivity: stepDetails[currentStepIndex]?.completed || false,
      isFullyComplete: completedSteps === totalSteps,
      isBrandNew: false,
      stepDetails: stepDetails
    };
  };

  const getProgressIcon = (progress) => {
    if (progress.isFullyComplete) return <CheckCircle2 className="h-4 w-4" />;
    if (progress.hasActivity || progress.percentage > 0) return <Activity className="h-4 w-4 animate-pulse" />;
    return <Zap className="h-4 w-4" />;
  };

  const getStepStatusText = (step, stepIndex, progress) => {
    if (step.completed) return 'Completed';
    if (stepIndex === progress.stepIndex) return 'Current Step';
    if (stepIndex < progress.stepIndex) return 'Completed';
    return 'Pending';
  };

  const getStepStatusIcon = (step, stepIndex, progress) => {
    if (step.completed) return <CheckCircle2 className="h-3 w-3 text-white" />;
    if (stepIndex === progress.stepIndex) return <Clock className="h-3 w-3 text-white" />;
    return null;
  };

  const getStepDotColor = (step, stepIndex, progress, stepColors) => {
    if (step.completed) return stepColors.tab + ' shadow-md';
    if (stepIndex === progress.stepIndex) return 'bg-blue-400 animate-pulse ring-2 ring-blue-200';
    return 'bg-gray-200';
  };

  const getProgressColors = (color, progress) => {
    const baseColors = {
      gray: {
        bg: 'bg-gradient-to-br from-gray-50 to-gray-100',
        text: 'text-gray-700',
        border: 'border-gray-200 hover:border-gray-300',
        icon: 'text-gray-500',
        tab: 'bg-gradient-to-r from-gray-300 to-gray-400',
        glow: 'shadow-gray-200/50'
      },
      blue: {
        bg: 'bg-gradient-to-br from-blue-50 to-cyan-50',
        text: 'text-blue-800',
        border: 'border-blue-200 hover:border-blue-400',
        icon: 'text-blue-600',
        tab: 'bg-gradient-to-r from-blue-400 to-cyan-500',
        glow: 'shadow-blue-200/50'
      },
      red: {
        bg: 'bg-gradient-to-br from-red-50 to-pink-50',
        text: 'text-red-800',
        border: 'border-red-200 hover:border-red-400',
        icon: 'text-red-600',
        tab: 'bg-gradient-to-r from-red-400 to-pink-500',
        glow: 'shadow-red-200/50'
      },
      orange: {
        bg: 'bg-gradient-to-br from-orange-50 to-yellow-50',
        text: 'text-orange-800',
        border: 'border-orange-200 hover:border-orange-400',
        icon: 'text-orange-600',
        tab: 'bg-gradient-to-r from-orange-400 to-yellow-500',
        glow: 'shadow-orange-200/50'
      },
      yellow: {
        bg: 'bg-gradient-to-br from-yellow-50 to-amber-50',
        text: 'text-yellow-800',
        border: 'border-yellow-200 hover:border-yellow-400',
        icon: 'text-yellow-600',
        tab: 'bg-gradient-to-r from-yellow-400 to-amber-500',
        glow: 'shadow-yellow-200/50'
      },
      green: {
        bg: 'bg-gradient-to-br from-green-50 to-emerald-50',
        text: 'text-green-800',
        border: 'border-green-200 hover:border-green-400',
        icon: 'text-green-600',
        tab: 'bg-gradient-to-r from-green-400 to-emerald-500',
        glow: 'shadow-green-200/50'
      }
    };
    
    return baseColors[color] || baseColors.gray;
  };

  useEffect(() => {
    const loadBatchProgress = async () => {
      if (!connections.length) return;
      try {
        const ids = connections.map(c => c.id);
        const progressData = await QuoteProgressService.getProgressBatch(ids);
        setProgressData(progressData);
      } catch (err) {
        console.error('Failed to load batch progress:', err);
      }
    };

    loadBatchProgress();
  }, [connections]);

  useEffect(() => {
    let filtered = connections;

    if (last6MonthsOnly) {
      const sixMonthsAgo = new Date();
      sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
      filtered = filtered.filter(c => new Date(c.contract_date) >= sixMonthsAgo);
    }

    if (searchTerm.trim() !== '') {
      const lowerSearch = searchTerm.toLowerCase();
      filtered = filtered.filter(connection => {
        return (
          (connection.company_name?.toLowerCase() || '').includes(lowerSearch) ||
          (connection.salesman_name?.toLowerCase() || '').includes(lowerSearch) ||
          (connection.md_name?.toLowerCase() || '').includes(lowerSearch) ||
          (connection.company_reg?.toLowerCase() || '').includes(lowerSearch)
        );
      });
    }

    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'date':
          return new Date(b.contract_date) - new Date(a.contract_date);
        case 'salesman':
          return (a.salesman_name || '').localeCompare(b.salesman_name || '');
        case 'company':
          return (a.company_name || '').localeCompare(b.company_name || '');
        default:
          return 0;
      }
    });

    setFilteredConnections(filtered);
    setCurrentPage(1);
  }, [searchTerm, sortBy, connections, last6MonthsOnly]);

  const totalPages = Math.ceil(filteredConnections.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentConnections = filteredConnections.slice(startIndex, startIndex + itemsPerPage);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const renderPaginationButtons = () => {
    const pages = [];
    const delta = 2;
    const left = currentPage - delta;
    const right = currentPage + delta;
    let lastPage = null;

    for (let i = 1; i <= totalPages; i++) {
      if (i === 1 || i === totalPages || (i >= left && i <= right)) {
        if (lastPage && i - lastPage > 1) {
          pages.push(
            <span key={`ellipsis-${i}`} className="px-2 py-1 text-gray-400 text-sm select-none">
              ...
            </span>
          );
        }
        pages.push(
          <button
            key={i}
            onClick={() => handlePageChange(i)}
            className={`min-w-[36px] px-3 py-1.5 border rounded-lg text-sm font-medium transition-all duration-200 ${
              currentPage === i
                ? 'bg-blue-500 text-white border-blue-500 shadow-sm'
                : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50 hover:border-gray-400'
            }`}
          >
            {i}
          </button>
        );
        lastPage = i;
      }
    }
    return pages;
  };

  const handleEditClick = (connection) => {
    setEditingConnection(connection);
    setFormData(connection);
    setShowModal(true);
  };

  const handleDeleteClick = (e, connection) => {
    e.stopPropagation();
    setDeleteConfirm(connection);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm) return;
    try {
      await connectionService.deleteConnection(deleteConfirm.id);
      await loadData();
      setDeleteSuccess(true);
      setTimeout(() => setDeleteSuccess(false), 3000);
    } catch (error) {
      console.error("Error deleting connection:", error);
    } finally {
      setDeleteConfirm(null);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    try {
      await connectionService.updateConnection(editingConnection.id, formData);
      await loadData();
      setEditingConnection(null);
    } catch (error) {
      console.error("Error updating connection:", error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[400px] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">

      {/* Delete Success Toast */}
      {deleteSuccess && (
        <div className="fixed top-6 right-6 z-50 flex items-center space-x-3 bg-green-600 text-white px-5 py-4 rounded-2xl shadow-2xl">
          <CheckCircle2 className="h-5 w-5 flex-shrink-0" />
          <div>
            <p className="font-bold text-sm">Connection Deleted</p>
            <p className="text-xs text-green-100">The connection and all related data have been removed.</p>
          </div>
          <button onClick={() => setDeleteSuccess(false)} className="ml-2 hover:text-green-200 transition-colors">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Header */}
      <div className="mb-6">
        <div className="flex justify-end mb-4">
          <button
            onClick={onClose}
            className="group flex items-center space-x-2 px-4 py-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-xl transition-all duration-200 border border-transparent hover:border-blue-200 shadow-sm hover:shadow-md"
          >
            <span className="font-medium">Back to Quotes</span>
            <ChevronRight className="h-5 w-5 rotate-180 group-hover:translate-x-1 transition-transform duration-200" />
          </button>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Select Existing Connection</h2>
        <p className="text-gray-600">Choose from your recent connections to create a new quote</p>
      </div>

      {/* Search + Sort + Filter */}
      <div className="mb-6 flex flex-col sm:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
          <input
            type="text"
            placeholder="Search by company, salesman, MD name, or registration..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        
        <div className="flex items-center space-x-2">
          <Filter className="h-5 w-5 text-gray-400" />
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
          >
            <option value="date">Sort by Date</option>
            <option value="salesman">Sort by Salesman</option>
            <option value="company">Sort by Company</option>
          </select>
        </div>

        <button
          onClick={() => setLast6MonthsOnly(prev => !prev)}
          className={`flex items-center space-x-2 px-4 py-3 rounded-lg border font-medium text-sm transition-all duration-200 ${
            last6MonthsOnly
              ? 'bg-blue-500 text-white border-blue-500 shadow-sm'
              : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
          }`}
        >
          <Calendar className="h-4 w-4" />
          <span>{last6MonthsOnly ? 'Last 6 Months' : 'All Time'}</span>
        </button>
      </div>

      {/* Progress Steps Overview — visible to all except Team Members */}
      {canViewProgress && (   
        <div className="mb-8 bg-white/80 backdrop-blur-sm p-6 rounded-3xl shadow-lg border border-gray-100">
          <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
            <TrendingUp className="h-5 w-5 mr-2 text-blue-600" />
            Progress Steps
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
            {STEP_SEQUENCE.map((step, index) => {
              const colors = getProgressColors(step.color);
              return (
                <div 
                  key={step.name} 
                  className="relative group cursor-help"
                >
                  <div className={`flex items-center space-x-3 p-3 rounded-xl hover:bg-gray-100 transition-colors ${
                    step.isInitial ? 'bg-blue-50 border-2 border-blue-200' : 'bg-gray-50'
                  }`}>
                    <div className={`w-4 h-4 ${colors.tab} rounded-full shadow-sm flex items-center justify-center text-white`}>
                      {step.icon}
                    </div>
                    <span className="text-xs font-medium text-gray-700 truncate">
                      {index + 1}. {step.label}
                    </span>
                  </div>
                  
                  {/* Tooltip */}
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-20 shadow-lg">
                    {step.label}
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Connections List */}
      <div className="space-y-4">
        {currentConnections.map((connection, index) => {
          const progress = getProgressLevel(connection.id);
          const colors = getProgressColors(progress.color, progress);
          
          return (
            <div 
              key={connection.id} 
              className={`group relative overflow-hidden bg-white/90 backdrop-blur-sm border-2 ${colors.border} rounded-3xl shadow-lg hover:shadow-2xl ${colors.glow} transition-all duration-500 transform hover:-translate-y-1`}
              style={{ 
                animationDelay: `${index * 100}ms`,
                animation: `slideInUp 0.6s ease-out forwards`
              }}
            >
              {/* Background tint — visible to all except Team Members */}
              {canViewProgress && (  
                <div className="absolute inset-0 opacity-5">
                  <div className={`w-full h-full ${colors.bg}`}></div>
                </div>
              )}
              
              {/* Progress tab badge — visible to all except Team Members */}
              {canViewProgress && ( 
                <div className={`absolute top-0 left-0 ${colors.tab} px-4 py-2 rounded-br-2xl shadow-lg z-10`}>
                  <div className="flex items-center space-x-2">
                    <div className="text-white">
                      {getProgressIcon(progress)}
                    </div>
                    <span className="text-sm font-bold text-white">
                      {progress.stepLabel}
                      {progress.isBrandNew && <span className="text-xs opacity-90"> (New)</span>}
                    </span>
                  </div>
                </div>
              )}
              
              <div className={`p-8 ${canViewProgress ? 'pt-12' : 'pt-8'}`}>
                <div 
                  className="flex-1 cursor-pointer"
                  onClick={() => handleConnectionSelect({ connection, stepName: progress.currentStep, isAdmin: isAdmin })}
                >
                  <div className="flex items-center justify-between mb-6">
                    {/* Left side: Company info */}
                    <div className="flex items-center space-x-4">
                      <div className={`${colors.bg} p-4 rounded-2xl shadow-md group-hover:shadow-lg transition-all duration-300 border border-gray-100`}>
                        <Building className={`h-6 w-6 ${colors.icon}`} />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-800 group-hover:text-gray-900 transition-colors">
                          {connection.company_name}
                        </h3>
                        <p className="text-gray-500 font-medium">{connection.company_reg}</p>
                      </div>
                    </div>

                    {/* Right side: Status + Arrow + Edit + Delete */}
                    <div className="flex items-center space-x-3">
                      <span className={`px-4 py-2 rounded-xl text-sm font-bold shadow-sm ${
                        connection.regulation_status === 'Active' 
                          ? 'bg-green-100 text-green-800 border border-green-200' 
                          : 'bg-yellow-100 text-yellow-800 border border-yellow-200'
                      }`}>
                        {connection.regulation_status}
                      </span>
                      
                      <ChevronRight className="h-6 w-6 text-gray-400 group-hover:text-blue-500 group-hover:translate-x-1 transition-all duration-300" />

                      <button 
                        onClick={(e) => { e.stopPropagation(); handleEditClick(connection); }}
                        className="p-3 rounded-xl bg-gray-100 hover:bg-blue-100 transition-all duration-300 shadow-sm hover:shadow-md group-hover:scale-105"
                      >
                        <Pencil className="h-4 w-4 text-gray-600 hover:text-blue-600" />
                      </button>

                      <button 
                        onClick={(e) => handleDeleteClick(e, connection)}
                        className="p-3 rounded-xl bg-gray-100 hover:bg-red-100 transition-all duration-300 shadow-sm hover:shadow-md group-hover:scale-105"
                      >
                        <Trash2 className="h-4 w-4 text-gray-600 hover:text-red-600" />
                      </button>
                    </div>
                  </div>

                  {/* Progress Bar — visible to all except Team Members */}
                  {canViewProgress && ( 
                    <div className="mb-6 relative">
                      <div className="w-full bg-gray-200 rounded-full h-3 shadow-inner">
                        <div 
                          className={`h-3 rounded-full ${colors.tab} transition-all duration-1000 ease-out shadow-sm relative overflow-hidden`}
                          style={{ width: `${progress.percentage}%` }}
                        >
                          <div className="absolute inset-0 bg-white opacity-30 animate-pulse"></div>
                        </div>
                      </div>
                      <div className="flex justify-between mt-2 text-xs text-gray-500">
                        <span>Progress</span>
                        <span className="font-bold">
                          {Math.round(progress.percentage)}% ({progress.completedSteps}/{STEP_SEQUENCE.length} steps)
                          {progress.isBrandNew && <span className="text-blue-600 ml-1">• Starting at System Config</span>}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Middle info */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm mb-6">
                    <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <User className="h-4 w-4 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-gray-500 text-xs font-medium">Managing Director</p>
                        <p className="font-bold text-gray-800">{connection.md_name}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                      <div className="p-2 bg-green-100 rounded-lg">
                        <Users className="h-4 w-4 text-green-600" />
                      </div>
                      <div>
                        <p className="text-gray-500 text-xs font-medium">Salesman</p>
                        <p className="font-bold text-gray-800">{connection.salesman_name}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                      <div className="p-2 bg-orange-100 rounded-lg">
                        <Calendar className="h-4 w-4 text-orange-600" />
                      </div>
                      <div>
                        <p className="text-gray-500 text-xs font-medium">Contract Date</p>
                        <p className="font-bold text-gray-800">{new Date(connection.contract_date).toLocaleDateString("en-GB")}</p>
                      </div>
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="flex items-center pt-4 border-t border-gray-100">
                    <div className="p-2 bg-purple-100 rounded-lg mr-3">
                      <Mail className="h-4 w-4 text-purple-600" />
                    </div>
                    <span className="text-sm font-medium text-gray-700">{connection.director_email}</span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {filteredConnections.length === 0 && (
        <div className="text-center py-12">
          <Building className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 text-lg">No connections found</p>
          <p className="text-gray-400">
            {last6MonthsOnly ? 'No connections in the last 6 months. Try switching to "All Time".' : 'Try adjusting your search terms'}
          </p>
          {last6MonthsOnly && (
            <button
              onClick={() => setLast6MonthsOnly(false)}
              className="mt-4 px-5 py-2 bg-blue-500 text-white rounded-lg text-sm font-medium hover:bg-blue-600 transition-colors"
            >
              Show All Time
            </button>
          )}
        </div>
      )}

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center mt-6 space-x-1">
          <button
            onClick={() => handlePageChange(1)}
            disabled={currentPage === 1}
            className="px-3 py-1.5 border rounded-lg text-sm font-medium bg-white text-gray-600 border-gray-300 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
          >
            «
          </button>
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="px-3 py-1.5 border rounded-lg text-sm font-medium bg-white text-gray-600 border-gray-300 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
          >
            Prev
          </button>

          {renderPaginationButtons()}

          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="px-3 py-1.5 border rounded-lg text-sm font-medium bg-white text-gray-600 border-gray-300 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
          >
            Next
          </button>
          <button
            onClick={() => handlePageChange(totalPages)}
            disabled={currentPage === totalPages}
            className="px-3 py-1.5 border rounded-lg text-sm font-medium bg-white text-gray-600 border-gray-300 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
          >
            »
          </button>
        </div>
      )}

      {/* Footer count */}
      <div className="mt-4 text-center text-sm text-gray-500">
        Showing {filteredConnections.length === 0 ? 0 : startIndex + 1}–{Math.min(startIndex + itemsPerPage, filteredConnections.length)} of {filteredConnections.length} connections
        {last6MonthsOnly && <span className="ml-1 text-blue-500 font-medium">(Last 6 months)</span>}
      </div>

      {/* Edit Modal */}
      {editingConnection && (
        <CustomerForm
          mode="edit"
          initialData={editingConnection}
          onClose={() => setEditingConnection(null)}
          onSaved={() => {
            loadData();
            setEditingConnection(null);
          }}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4">
            <div className="flex items-center space-x-3 mb-4">
              <div className="p-3 bg-red-100 rounded-xl">
                <Trash2 className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900">Delete Connection</h3>
            </div>
            <p className="text-gray-600 mb-2">
              Are you sure you want to delete{' '}
              <span className="font-bold text-gray-900">{deleteConfirm.company_name}</span>?
            </p>
            <p className="text-sm text-red-500 mb-6">
              This will permanently delete this entry. This action cannot be undone.
            </p>
            <div className="flex space-x-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 px-4 py-3 border border-gray-300 rounded-xl text-gray-700 font-medium hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                className="flex-1 px-4 py-3 bg-red-600 hover:bg-red-700 text-white rounded-xl font-medium transition-colors shadow-sm"
              >
                Delete Connection
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default ExistingComponent;